import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { ChatIcon } from '@/components/layout/icons';
import { submitFeedback } from '@shared/api/feedback';
import { apiErrorMessage } from '@shared/api/client';

export function FeedbackWidget() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: () => submitFeedback({ content: content.trim() }),
    onSuccess: () => {
      setShowSuccess(true);
    },
  });

  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setIsOpen(false);
        // Reset state after closing animation
        setTimeout(() => {
          setContent('');
          setShowSuccess(false);
          mutation.reset();
        }, 300);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess, mutation]);

  if (location.pathname.includes('/chat')) {
    return null;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!content.trim() || mutation.isPending || showSuccess) return;
    mutation.mutate();
  }

  function handleClose() {
    if (mutation.isPending) return;
    setIsOpen(false);
    setContent('');
    setShowSuccess(false);
    mutation.reset();
  }

  return (
    <>
      {/* Floating trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-40 hidden md:flex items-center justify-center h-12 w-12 rounded-full bg-brand-600 text-white shadow-lg hover:bg-brand-700 focus:outline-none transition-transform hover:scale-105 active:scale-95"
        title="Share Feedback"
      >
        <ChatIcon className="h-6 w-6" />
      </button>

      {/* Modal overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-md rounded-2xl border border-gray-100 shadow-xl overflow-hidden p-6 relative animate-in fade-in zoom-in-95 duration-200">
            {showSuccess ? (
              <div className="text-center py-6 space-y-3">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 text-green-600">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Thank you!</h3>
                <p className="text-sm text-gray-600">
                  Your feedback has been submitted successfully.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Share your feedback</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    How is your experience with EcRegi? We&rsquo;d love to hear your thoughts,
                    suggestions, or issues.
                  </p>
                </div>

                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Tell us about your experience..."
                  required
                  rows={4}
                  className="w-full p-3 text-sm text-gray-900 placeholder:text-gray-400 bg-white border border-gray-200 rounded-lg focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 resize-none"
                />

                {mutation.isError && (
                  <p className="text-xs text-red-600">
                    {apiErrorMessage(
                      mutation.error,
                      'Failed to submit feedback. Please try again.',
                    )}
                  </p>
                )}

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={mutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <Button type="submit" disabled={mutation.isPending || !content.trim()}>
                    {mutation.isPending ? 'Submitting…' : 'Submit'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
